// Masjid Printable Poster — single-page A4 poster designed to be printed
// and stuck on a masjid notice board, community-centre wall, or shared as
// a hand-out. The whole thing is generated client-side via jsPDF +
// qrcode — no server, no Internet roundtrip, no CORS issues.
//
// Layout (210 × 297 mm portrait A4):
//   ┌──────────────────────────────────────────┐
//   │  Header band (emerald)                   │
//   │   [logo] Amanah by Zakat.International   url right │
//   ├──────────────────────────────────────────┤
//   │   Brand mantra: CALCULATE · PLAN · FULFILL│
//   │   Huge headline                           │
//   │   Subhead (1 line)                        │
//   │                                           │
//   │   ┌────────────────────────────┐          │
//   │   │ ZAKAT       2.5% · live …  │          │
//   │   │ FARAID      Islamic inh… │          │
//   │   │ WASIYYAH    Scholar-veri…│          │
//   │   └────────────────────────────┘          │
//   │                                           │
//   │      ┌──────────────────────┐             │
//   │      │     QR CODE 110mm    │             │
//   │      └──────────────────────┘             │
//   │   zakat.international                      │
//   │                                           │
//   │   ── English hadith (italic) — citation ── │
//   │                                           │
//   │   Tagline · No accounts · Free forever     │
//   └──────────────────────────────────────────┘
import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import { LOGO_BASE64 } from '../config/logoBase64';

const COLORS = {
  emerald: '#0F4C3A',
  emeraldDark: '#0A3429',
  gold: '#C9A961',
  goldDark: '#8A7240',
  goldLight: '#F0E6C8',
  stoneDark: '#3A332B',
  stone: '#78716C',
  stoneLight: '#F5F5F4',
  white: '#FFFFFF',
};

const DEFAULT_HADITHS = [
  {
    english: 'Verily, deeds are by intentions.',
    citation: 'Sahih al-Bukhari 1',
  },
  {
    english: 'A Muslim is the brother of another Muslim.',
    citation: 'Sahih al-Bukhari 2442',
  },
  {
    english: 'The believer is not one who eats his fill while his neighbour goes hungry.',
    citation: 'Adab al-Mufrad 112',
  },
];

// -------------------------------------------------------------------
// Poster copy localised into Latin-script languages.
// jsPDF's bundled Helvetica cannot render Arabic / Devanagari / Bengali /
// Cyrillic / etc., so we deliberately limit the picker to scripts that
// render natively. Adding more languages would require embedding a multi-
// script font (~3–5 MB per language) which is deferred.
// -------------------------------------------------------------------
export const POSTER_LANGS = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'ms', label: 'Bahasa Malaysia' },
  { code: 'id', label: 'Bahasa Indonesia' },
  { code: 'tr', label: 'Türkçe' },
];

const POSTER_COPY = {
  en: {
    headerTagline: 'Free · Privacy-first · No accounts',
    mantra:        'CALCULATE  ·  PLAN  ·  FULFILL',
    headline:      'Three free Islamic financial tools.',
    subhead:       'Calculate Zakat. Plan inheritance. Write your will. All in your browser.',
    zakatName:     'ZAKAT',
    zakatDesc:     '2.5% of wealth  ·  53 currencies  ·  live nisab',
    faraidName:    'FARAID',
    faraidDesc:    'Islamic inheritance  ·  every major school  ·  awl & radd',
    wasiyyahName:  'WASIYYAH',
    wasiyyahDesc:  'Sharia-aligned will  ·  1/3 cap  ·  guardianship  ·  family letter',
    scanTagline:   'Scan with your phone camera to open all three calculators.',
    distributedBy: 'Distributed by',
    footer:        'No accounts  ·  No tracking  ·  100% in your browser  ·  Free forever',
  },
  fr: {
    headerTagline: 'Gratuit · Confidentialité · Sans compte',
    mantra:        'CALCULER  ·  PLANIFIER  ·  ACCOMPLIR',
    headline:      'Trois outils financiers islamiques gratuits.',
    subhead:       'Calculez la Zakat. Planifiez l\u2019héritage. Rédigez votre testament. Dans votre navigateur.',
    zakatName:     'ZAKAT',
    zakatDesc:     '2,5% de la richesse  ·  53 devises  ·  nisab en direct',
    faraidName:    'FARA\u2019ID',
    faraidDesc:    'Héritage islamique  ·  toutes les grandes écoles  ·  awl & radd',
    wasiyyahName:  'WASIYYAH',
    wasiyyahDesc:  'Testament conforme à la charia  ·  limite 1/3  ·  tutelle  ·  lettre familiale',
    scanTagline:   'Scannez avec votre téléphone pour ouvrir les trois calculatrices.',
    distributedBy: 'Distribué par',
    footer:        'Sans compte  ·  Sans suivi  ·  100% dans votre navigateur  ·  Gratuit pour toujours',
  },
  ms: {
    headerTagline: 'Percuma · Privasi dahulu · Tanpa akaun',
    mantra:        'KIRA  ·  RANCANG  ·  TUNAIKAN',
    headline:      'Tiga alat kewangan Islam percuma.',
    subhead:       'Kira Zakat. Rancang pusaka. Tulis wasiat anda. Semuanya dalam pelayar anda.',
    zakatName:     'ZAKAT',
    zakatDesc:     '2.5% harta  ·  53 mata wang  ·  nisab terkini',
    faraidName:    'FARAID',
    faraidDesc:    'Pusaka Islam  ·  setiap mazhab utama  ·  awl & radd',
    wasiyyahName:  'WASIAT',
    wasiyyahDesc:  'Wasiat patuh syariah  ·  had 1/3  ·  penjagaan anak  ·  surat keluarga',
    scanTagline:   'Imbas dengan kamera telefon untuk membuka ketiga-tiga kalkulator.',
    distributedBy: 'Diedarkan oleh',
    footer:        'Tanpa akaun  ·  Tanpa penjejakan  ·  100% dalam pelayar  ·  Percuma selama-lamanya',
  },
  id: {
    headerTagline: 'Gratis · Privasi utama · Tanpa akun',
    mantra:        'HITUNG  ·  RENCANAKAN  ·  TUNAIKAN',
    headline:      'Tiga alat keuangan Islam gratis.',
    subhead:       'Hitung Zakat. Rencanakan warisan. Tulis wasiat Anda. Semua di peramban Anda.',
    zakatName:     'ZAKAT',
    zakatDesc:     '2,5% harta  ·  53 mata uang  ·  nisab terkini',
    faraidName:    'FARAID',
    faraidDesc:    'Warisan Islam  ·  setiap mazhab utama  ·  awl & radd',
    wasiyyahName:  'WASIAT',
    wasiyyahDesc:  'Wasiat sesuai syariah  ·  batas 1/3  ·  perwalian  ·  surat keluarga',
    scanTagline:   'Pindai dengan kamera ponsel untuk membuka ketiga kalkulator.',
    distributedBy: 'Disebarkan oleh',
    footer:        'Tanpa akun  ·  Tanpa pelacakan  ·  100% di peramban Anda  ·  Gratis selamanya',
  },
  tr: {
    headerTagline: 'Ücretsiz · Gizlilik öncelikli · Hesapsız',
    mantra:        'HESAPLA  ·  PLANLA  ·  YERİNE GETİR',
    headline:      'Üç ücretsiz İslami finans aracı.',
    subhead:       'Zekat hesaplayın. Mirası planlayın. Vasiyetinizi yazın. Tarayıcınızda.',
    zakatName:     'ZEKAT',
    zakatDesc:     'Servetin %2,5\u2019i  ·  53 para birimi  ·  güncel nisab',
    faraidName:    'FERAİZ',
    faraidDesc:    'İslami miras  ·  tüm büyük mezhepler  ·  avl ve redd',
    wasiyyahName:  'VASİYET',
    wasiyyahDesc:  'Şeriata uygun vasiyet  ·  1/3 sınırı  ·  vesayet  ·  aile mektubu',
    scanTagline:   'Üç hesaplayıcıyı açmak için telefon kameranızla tarayın.',
    distributedBy: 'Dağıtan',
    footer:        'Hesapsız  ·  Takipsiz  ·  %100 tarayıcınızda  ·  Sonsuza dek ücretsiz',
  },
};

const resolveCopy = (lang) => POSTER_COPY[lang] || POSTER_COPY.en;

const buildQrDataUrl = async (url) => (
  QRCode.toDataURL(url, {
    errorCorrectionLevel: 'M',
    width: 700,
    margin: 1,
    color: { dark: COLORS.emeraldDark, light: COLORS.white },
  })
);

const drawHeaderBand = (doc, copy) => {
  const W = doc.internal.pageSize.getWidth();
  doc.setFillColor(COLORS.emerald);
  doc.rect(0, 0, W, 24, 'F');
  // Gold accent line under the header
  doc.setFillColor(COLORS.gold);
  doc.rect(0, 24, W, 1.4, 'F');

  // Logo (small, square)
  try { doc.addImage(LOGO_BASE64, 'PNG', 12, 5, 14, 14); } catch (e) { /* non-fatal */ }

  // Brand wordmark
  doc.setTextColor(COLORS.white);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.text('Amanah by Zakat.International', 30, 15);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(COLORS.goldLight);
  doc.text(copy.headerTagline, 30, 20);

  // URL right
  doc.setTextColor(COLORS.white);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('zakat.international', W - 12, 16, { align: 'right' });
};

const drawFooter = (doc, copy) => {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  doc.setFillColor(COLORS.stoneLight);
  doc.rect(0, H - 14, W, 14, 'F');
  doc.setTextColor(COLORS.stone);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(copy.footer, W / 2, H - 5, { align: 'center' });
};

// Render one tool row: bold name on the left, description on the right.
// All anchored to a fixed left column so names align visually.
const drawToolRow = (doc, name, desc, y, leftX, nameColWidth) => {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(COLORS.emeraldDark);
  doc.text(name, leftX, y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(COLORS.stoneDark);
  doc.text(desc, leftX + nameColWidth, y);
};

/**
 * Build the masjid poster and trigger a download.
 *
 * @param {object} opts
 * @param {string} [opts.url]
 * @param {string} [opts.headline]
 * @param {object} [opts.hadith] — `{ english, citation }`. Arabic is omitted on
 *                                  purpose: jsPDF's bundled Helvetica cannot
 *                                  render Arabic glyphs, and an embedded
 *                                  Arabic-capable font would balloon the file.
 * @param {boolean} [opts.richMode]
 * @param {string} [opts.masjidName]
 */
export const downloadMasjidPosterPdf = async ({
  url = 'https://zakat.international',
  headline,
  hadith = DEFAULT_HADITHS[0],
  richMode = true,
  masjidName = '',
  lang = 'en',
} = {}) => {
  const copy = resolveCopy(lang);
  const finalHeadline = headline || copy.headline;
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  drawHeaderBand(doc, copy);

  // Brand mantra — small, dignified, mono-style above the headline
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(COLORS.goldDark);
  doc.text(copy.mantra, W / 2, 38, { align: 'center' });

  // Massive headline — bold, tight leading
  doc.setTextColor(COLORS.emeraldDark);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(30);
  const headlineLines = doc.splitTextToSize(finalHeadline, W - 30);
  let cursorY = 52;
  headlineLines.forEach((line) => {
    doc.text(line, W / 2, cursorY, { align: 'center' });
    cursorY += 11;
  });

  // Subhead — one elegant line
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  doc.setTextColor(COLORS.stoneDark);
  const subheadLines = doc.splitTextToSize(copy.subhead, W - 30);
  subheadLines.forEach((line) => {
    doc.text(line, W / 2, cursorY + 3, { align: 'center' });
    cursorY += 6;
  });
  cursorY += 10;

  // Tools block — two-column layout with localised names + descriptions.
  const blockLeft = 24;
  const nameCol = 34;
  const rowHeight = 9;

  doc.setDrawColor(COLORS.gold);
  doc.setLineWidth(0.35);
  doc.line(blockLeft, cursorY, W - blockLeft, cursorY);
  cursorY += 7;

  drawToolRow(doc, copy.zakatName,    copy.zakatDesc,    cursorY,                   blockLeft, nameCol);
  drawToolRow(doc, copy.faraidName,   copy.faraidDesc,   cursorY + rowHeight,       blockLeft, nameCol);
  drawToolRow(doc, copy.wasiyyahName, copy.wasiyyahDesc, cursorY + rowHeight * 2,   blockLeft, nameCol);

  cursorY += rowHeight * 2 + 7;
  doc.setDrawColor(COLORS.gold);
  doc.setLineWidth(0.35);
  doc.line(blockLeft, cursorY, W - blockLeft, cursorY);
  cursorY += 6;

  // QR code (huge, centered) — bordered tile
  const qrDataUrl = await buildQrDataUrl(url);
  const qrSize = 92; // mm — slightly smaller to leave room for tools + hadith
  const qrX = (W - qrSize) / 2;
  const qrY = cursorY + 4;

  doc.setFillColor(COLORS.goldLight);
  doc.roundedRect(qrX - 4, qrY - 4, qrSize + 8, qrSize + 8, 3, 3, 'F');
  doc.setDrawColor(COLORS.gold);
  doc.setLineWidth(0.6);
  doc.roundedRect(qrX - 4, qrY - 4, qrSize + 8, qrSize + 8, 3, 3);
  doc.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);

  // Short URL under the QR
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(COLORS.emerald);
  doc.text('zakat.international', W / 2, qrY + qrSize + 10, { align: 'center' });

  // Optional hadith band (English-only — Arabic glyphs aren't supported by
  // the default jsPDF font and would render as gibberish).
  if (richMode && hadith) {
    const bandY = qrY + qrSize + 18;
    if (bandY + 18 < H - 28) {
      doc.setDrawColor(COLORS.gold);
      doc.setLineWidth(0.3);
      doc.line(50, bandY, W - 50, bandY);

      doc.setFont('helvetica', 'italic');
      doc.setFontSize(11);
      doc.setTextColor(COLORS.emeraldDark);
      doc.text(`“${hadith.english}”`, W / 2, bandY + 7, { align: 'center' });

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(COLORS.stone);
      doc.text(hadith.citation.toUpperCase(), W / 2, bandY + 12, { align: 'center' });

      doc.line(50, bandY + 16, W - 50, bandY + 16);
    }
  }

  // Tagline above the footer
  doc.setFontSize(10);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(COLORS.stone);
  doc.text(copy.scanTagline, W / 2, H - 20, { align: 'center' });

  if (masjidName) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(COLORS.stone);
    doc.text(`${copy.distributedBy} ${masjidName}`, W / 2, H - 16, { align: 'center' });
  }

  drawFooter(doc, copy);

  const safeName = (masjidName || 'masjid').replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase();
  doc.save(`zakat-masjid-poster-${safeName}-${lang}-${new Date().toISOString().slice(0, 10)}.pdf`);
};

export const PRESET_HADITHS = DEFAULT_HADITHS;
